/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import DS from 'ember-data';
import WebhookModel from 'cio/models/webhook';
import FormValidations from 'cio/utils/form-validations';
import isCIODomain from 'cio/utils/validators/cio-domain';
import isValidUrl from 'cio/utils/validators/url';
import { ToggleDelta } from '@customerio/fly-components/addon/components/fly-grouped-checklist';
import WEBHOOK_EVENTS from 'cio/utils/webhook-events';
import AlertsService from 'cio/services/alerts';
import VerifyService from 'cio/services/verify';
import AnalyticsService from 'cio/services/analytics';
import { Task } from 'ember-concurrency';

enum ModalState {
  Closed = 'closed',
  Warning = 'warning',
}

enum SaveAction {
  ForceEnableSave = 'true',
  Save = 'false',
  Default = 'default',
}

interface ReportingWebhooksFormArgs {
  webhook: WebhookModel;
  allWebhooks?: WebhookModel[];
  readOnly: boolean;
  onCancel: () => unknown;
  onSave: Task<unknown, []>;
}

const Validations = {
  name(value?: string) {
    if (value?.length && !value?.trim()) {
      return "Name can't be blank";
    }

    if ((value?.length ?? 0) > 191) {
      return "Name can't be longer than 191 characters";
    }

    return true;
  },

  endpointUrl(value?: string) {
    if (!value?.trim()) {
      return "Endpoint URL can't be blank";
    } else if (isCIODomain(value)) {
      return "Endpoint URL can't be a Customer.io domain";
    } else if (!isValidUrl(value)) {
      return 'Endpoint URL must be a valid URL';
    }

    return true;
  },
};

export default class ReportingWebhooksForm extends Component<ReportingWebhooksFormArgs> {
  @service alerts!: AlertsService;
  @service verify!: VerifyService;
  @service store!: DS.Store;
  @service analytics!: AnalyticsService;

  @tracked modal = ModalState.Closed;
  readonly serverDisabled: boolean;
  readonly validations: FormValidations;
  readonly groups = WEBHOOK_EVENTS;

  constructor(owner: unknown, args: ReportingWebhooksFormArgs) {
    super(owner, args);

    // Store the original state from the server to check later if the disabled
    // state has changed
    this.serverDisabled = !args.webhook.enabled;

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'args.webhook',
    });
  }

  willDestroy() {
    super.willDestroy();

    this.verify.verifyReportingWebhook.cancelAll({ resetState: true });
  }

  get readOnly() {
    return this.args.readOnly ?? false;
  }

  get isFormDisabled() {
    return this.args.onSave.isRunning || this.readOnly;
  }

  get isSaveDisabled() {
    return (
      this.isFormDisabled ||
      this.validations.hasErrors ||
      !this.args.webhook.hasDirtyAttributes ||
      !this.args.webhook.endpointUrl
    );
  }

  get isSendTestDisabled() {
    return (
      this.validations.errors.endpointUrl ||
      !this.args.webhook.endpointUrl ||
      this.verify.verifyReportingWebhook.isRunning ||
      this.isFormDisabled
    );
  }

  get isRegenerateSigningKeyDisabled() {
    return this.isFormDisabled || this.regenerateSigningKey.isRunning;
  }

  get isNewWebhook() {
    // Webhooks are only assigned a signingKey during creation, so this is a way
    // to tell if it's new or not
    return !this.args.webhook.signingKey;
  }

  get events() {
    return this.args.webhook.events ?? [];
  }

  get existingEndpoint() {
    return this.args.allWebhooks?.any((webhook) => {
      return webhook.id !== this.args.webhook.id && this.args.webhook.endpointUrl === webhook.endpointUrl;
    });
  }

  get analyticsMeta() {
    return {
      name: 'Reporting Webhooks',
      category: 'Data management',
      endpointId: this.args.webhook.id,
      source: 'Webhook Edit Page',
    };
  }

  @action
  sendTest() {
    let { id, withContent, endpointUrl: url, signingKey: key } = this.args.webhook;
    this.verify.verifyReportingWebhook.perform({ id, url, key, withContent });
  }

  @action
  setResolution(value: boolean) {
    this.args.webhook.set('resolution', value);
  }

  @action
  setSelectedEvents({ selectedItems }: ToggleDelta) {
    this.args.webhook.set('events', selectedItems);
  }

  @action
  onSigningKeyCopy() {
    this.analytics.track('integration', 'reporting_webhook_key_copied', this.analyticsMeta);
  }

  @action
  async verifySaveWithEnable(saveAction: SaveAction, evt?: Event) {
    evt?.preventDefault();

    try {
      await this.validations.validateModel();

      if (saveAction !== SaveAction.Default) {
        this.args.webhook.set('enabled', saveAction === SaveAction.ForceEnableSave);

        if (saveAction === SaveAction.ForceEnableSave) {
          this.modal = ModalState.Warning;

          return;
        }
      } else if (this.serverDisabled && this.args.webhook.enabled && !this.args.webhook.isForceDisabled) {
        this.modal = ModalState.Warning;

        return;
      }

      return this.onSave();
    } catch {
      // This will be a validation error, which doesn't need to be caught.
      // API errors are handled upstream in the parent controller which has a task
    }

    return;
  }

  @action
  onSave() {
    return this.args.onSave.perform();
  }

  @action
  onReset() {
    if (!this.args.webhook.get('isNew')) {
      this.args.webhook.rollbackAttributes();
    } else {
      this.args.webhook.unloadRecord();
    }
    this.args.onCancel();
  }

  @action
  toggleEnabled() {
    let allEnabledWebhooks = this.store.peekAll('webhook').filterBy('enabled', true);

    this.args.webhook.set('enabled', !this.args.webhook.enabled);

    if (this.args.webhook.enabled) {
      this.analytics.track('integration', 'enable_started', {
        ...this.analyticsMeta,
        enabledOrReenabled: this.isNewWebhook ? 'Enabled' : 'Re-Enabled',
      });
    } else {
      this.analytics.track('integration', 'disable_started', {
        ...this.analyticsMeta,
        disabledOrDeleted: allEnabledWebhooks.length === 1 ? 'Disabled' : undefined,
        endpointDisabledOrDeleted: 'Disabled',
      });
    }
  }

  @action
  closeModal() {
    this.args.webhook.set('enabled', false);
    this.modal = ModalState.Closed;
  }

  @action
  trimAndValidateEndpoint(endpointUrl: string) {
    this.args.webhook.set('endpointUrl', endpointUrl.trim());
    this.validations.validateAttribute('endpointUrl');
  }

  regenerateSigningKey = task({ drop: true }, async () => {
    try {
      await this.args.webhook.regenerateSigningKey({});
    } catch {
      this.alerts.error(
        'integrations.reporting-webhooks.signing-key.error',
        'We ran into an issue creating a new signing key. Please try again.',
      );
    }
  });
}
